<script lang="ts">
	/**
	 * The store's numbers (Design §13, §16).
	 *
	 * Every money figure here is a **list keyed by currency** and is rendered as one, because the
	 * store genuinely holds several: a single MRR number would either be a sum of incompatible
	 * units or one currency silently standing in for all of them. Counts (subscriptions, paying
	 * customers) are cross-currency by nature and are the only scalars on the panel.
	 *
	 * `churnRate` and `refundRate` arrive as fractions — 0.05 is five per cent — so they are
	 * multiplied here rather than being printed as "0.05%".
	 */

	import { onMount } from 'svelte';
	import { Button, Table, TableBody, TableBodyCell, TableBodyRow, TableHead, TableHeadCell } from 'flowbite-svelte';
	import { EmptyState, Panel } from '../../../components';
	import { getMetrics } from '../../../AppNetwork';
	import { formatAmount } from '../../../stores/storefront';
	import type { StoreMetrics } from '../../../AppTypes';
	import CurrencyFigure from '../../../components/CurrencyFigure.svelte';

	let metrics: StoreMetrics | null = null;
	let loading: boolean = true;
	let loadError: string = '';

	async function fnLoad(): Promise<void> {
		loading = true;
		loadError = '';
		try {
			metrics = await getMetrics();
		} catch (error) {
			loadError = (error as Error).message;
		} finally {
			loading = false;
		}
	}

	/** A fraction on the wire, a percentage on the screen. */
	function fnPercent(fraction: number): string {
		return `${(fraction * 100).toFixed(1)}%`;
	}

	function fnMoney(amount: number, currency: string): string {
		return $formatAmount(amount, currency) ?? `${amount} minor units`;
	}

	$: counters = metrics === null ? [] : [
		{ label: 'Active subscriptions', value: metrics.activeSubscriptions, icon: 'circle-check' },
		{ label: 'Trialing', value: metrics.trialingSubscriptions, icon: 'hourglass-half' },
		{ label: 'Past due', value: metrics.pastDueSubscriptions, icon: 'triangle-exclamation' },
		{ label: 'Cancelled (30 days)', value: metrics.canceledLast30Days, icon: 'circle-xmark' },
		{ label: 'Paying customers', value: metrics.payingCustomers, icon: 'users' }
	];

	onMount(() => fnLoad());
</script>

<Panel title="Metrics" faIcon="chart-line">
	<svelte:fragment slot="titleRight">
		<Button size="xs" color="alternative" on:click={() => fnLoad()}>
			<i class="fa-solid fa-arrows-rotate mr-2" />Refresh
		</Button>
	</svelte:fragment>

	{#if loading}
		<EmptyState variant="loading" title="Loading the metrics…" />
	{:else if loadError.length > 0}
		<EmptyState variant="error" title="The metrics could not be loaded." description={loadError}>
			<Button color="alternative" size="sm" on:click={() => fnLoad()}>Try again</Button>
		</EmptyState>
	{:else if metrics === null}
		<EmptyState title="No metrics yet." description="Nothing has been sold, so there is nothing to measure." />
	{:else}
		<div class="space-y-4">
			<p class="text-default text-sm">
				Money is reported <strong>per currency</strong>. Figures in different currencies are never
				added together, because a combined total would be a number in no currency at all.
			</p>

			<div class="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
				{#each counters as counter (counter.label)}
					<div class="widget bg-depth">
						<div class="card-label">
							<i class="fa-solid fa-{counter.icon} text-[11px] mr-1 text-default" />{counter.label}
						</div>
						<div class="text-2xl font-semibold mt-1">{counter.value}</div>
					</div>
				{/each}
			</div>

			<div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
				<CurrencyFigure
					label="MRR"
					faIcon="repeat"
					amounts={metrics.mrr}
					emptyText="No recurring revenue."
				/>
				<CurrencyFigure
					label="Revenue (30 days)"
					faIcon="sack-dollar"
					amounts={metrics.revenueLast30Days}
					emptyText="No revenue in the last 30 days."
					description="Money collected in the 30 days up to now, before refunds — the Reports gross, over a rolling window."
				/>
				<CurrencyFigure
					label="Refunds (30 days)"
					faIcon="rotate-left"
					amounts={metrics.refundedLast30Days}
					description="Money the store gave back, on purpose."
					emptyText="No refunds in the last 30 days."
				/>
				<CurrencyFigure
					label="Chargebacks (30 days)"
					faIcon="gavel"
					tone="warning"
					amounts={metrics.chargebacksLast30Days}
					description="Money taken back by the cardholder's bank: a dispute, not a refund. Counted apart from refunds because it carries a fee and a decision the store did not make."
					emptyText="No chargebacks in the last 30 days."
				/>
			</div>

			<div class="grid grid-cols-1 md:grid-cols-3 gap-3">
				<div class="widget bg-depth">
					<div class="card-label"><i class="fa-solid fa-arrow-trend-down text-[11px] mr-1 text-default" />Churn rate</div>
					<div class="text-2xl font-semibold mt-1">{fnPercent(metrics.churnRate)}</div>
					<p class="text-default text-xs mt-2">Subscriptions cancelled in the last 30 days, as a share of those active.</p>
				</div>

				<div class="widget bg-depth">
					<div class="card-label"><i class="fa-solid fa-percent text-[11px] mr-1 text-default" />Refund rate</div>
					{#if metrics.refundRate.length === 0}
						<div class="text-default text-sm mt-1">No refunds to rate.</div>
					{:else}
						<div class="mt-1 space-y-1">
							{#each metrics.refundRate as rate (rate.currency)}
								<div class="flex items-baseline justify-between gap-3">
									<span class="text-default font-mono text-xs uppercase">{rate.currency}</span>
									<span class="text-lg font-semibold">{fnPercent(rate.rate)}</span>
								</div>
							{/each}
						</div>
					{/if}
					<p class="text-default text-xs mt-2">Rated within each currency, never across them.</p>
				</div>

				<div class="widget bg-depth">
					<div class="card-label"><i class="fa-solid fa-credit-card text-[11px] mr-1 text-default" />Failed payments</div>
					{#if metrics.failedPayments.length === 0}
						<div class="text-default text-sm mt-1">No failed payments.</div>
					{:else}
						<div class="mt-1 space-y-1">
							{#each metrics.failedPayments as failure (failure.currency)}
								<div class="flex items-baseline justify-between gap-3">
									<span class="text-default font-mono text-xs uppercase">{failure.currency}</span>
									<span class="text-lg font-semibold">{failure.count}</span>
								</div>
							{/each}
						</div>
					{/if}
					<p class="text-default text-xs mt-2">Counted per currency, since a failure belongs to the charge it was for.</p>
				</div>
			</div>

			<div class="grid grid-cols-1 xl:grid-cols-2 gap-3">
				<div class="widget bg-depth">
					<div class="card-label mb-2"><i class="fa-solid fa-box text-[11px] mr-1 text-default" />Revenue by product</div>
					{#if metrics.revenueByProduct.length === 0}
						<EmptyState compact title="No product revenue." />
					{:else}
						<div class="relative overflow-x-auto">
							<Table>
								<TableHead>
									<TableHeadCell>Product</TableHeadCell>
									<TableHeadCell>Currency</TableHeadCell>
									<TableHeadCell>Revenue</TableHeadCell>
									<TableHeadCell>Orders</TableHeadCell>
								</TableHead>
								<TableBody>
									{#each metrics.revenueByProduct as row (`${row.productUUID}-${row.currency}`)}
										<TableBodyRow>
											<TableBodyCell class="dataTable">{row.productName}</TableBodyCell>
											<TableBodyCell class="dataTable font-mono text-xs uppercase">{row.currency}</TableBodyCell>
											<TableBodyCell class="dataTable">{fnMoney(row.amount, row.currency)}</TableBodyCell>
											<TableBodyCell class="dataTable">{row.orders}</TableBodyCell>
										</TableBodyRow>
									{/each}
								</TableBody>
							</Table>
						</div>
					{/if}
				</div>

				<div class="widget bg-depth">
					<div class="card-label mb-2"><i class="fa-solid fa-calendar text-[11px] mr-1 text-default" />Revenue by period</div>
					{#if metrics.revenueByPeriod.length === 0}
						<EmptyState compact title="No period revenue." />
					{:else}
						<div class="relative overflow-x-auto">
							<Table>
								<TableHead>
									<TableHeadCell>Period</TableHeadCell>
									<TableHeadCell>Currency</TableHeadCell>
									<TableHeadCell>Revenue</TableHeadCell>
									<TableHeadCell>Invoices</TableHeadCell>
								</TableHead>
								<TableBody>
									{#each metrics.revenueByPeriod as row (`${row.period}-${row.currency}`)}
										<TableBodyRow>
											<TableBodyCell class="dataTable">{row.period}</TableBodyCell>
											<TableBodyCell class="dataTable font-mono text-xs uppercase">{row.currency}</TableBodyCell>
											<TableBodyCell class="dataTable">{fnMoney(row.amount, row.currency)}</TableBodyCell>
											<TableBodyCell class="dataTable">{row.invoices}</TableBodyCell>
										</TableBodyRow>
									{/each}
								</TableBody>
							</Table>
						</div>
					{/if}
				</div>
			</div>
		</div>
	{/if}
</Panel>
